import { useEffect, useState } from 'react'

interface RotationState {
    resetKey: string
    index: number
}

/**
 * Cycles an index through `0..length-1` every `intervalMs` when `length > 1`.
 * The index returns to 0 whenever `resetKey` changes. The reset is derived
 * during render (state remembers the key it was written under), so no effect
 * or extra render is needed to adjust state on a prop change.
 */
export function useRotatingIndex(
    length: number,
    resetKey: string,
    intervalMs: number,
): number {
    const [state, setState] = useState<RotationState>({ resetKey, index: 0 })

    useEffect(() => {
        if (length <= 1) return

        const interval = setInterval(() => {
            setState((prev) => ({
                resetKey,
                index:
                    prev.resetKey === resetKey ? (prev.index + 1) % length : 1,
            }))
        }, intervalMs)

        return () => clearInterval(interval)
    }, [length, resetKey, intervalMs])

    return state.resetKey === resetKey ? state.index : 0
}
